import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api/api';
import type {
  DefinePermissionInput,
  ReplaceSetPermissionsInput,
} from '@/lib/api/domains/permissions';
import { keys } from '@/lib/query-keys';

/*
 * Define-permission mutation (F03 R41). Mutate → invalidate: on success the
 * catalog is invalidated so the new permission appears (AC-51f2752f, and the
 * description against it where present — AC-3ce30755). A duplicate
 * `resource.action` is a 409 Identity returns with the catalog unchanged
 * (AC-de3346c0, AC-b7e7e3e5); the mutation does NOT swallow it — the error
 * propagates as an `ApiError` so the dialog can surface the reason where the
 * administrator works and treat the save as failed, never as success.
 *
 * No optimistic update: like every admin write, mutate then invalidate. The
 * record-kind FK check, the free-text action rule and the duplicate refusal are
 * Identity's (relayed by the domain service); the frontend sends the definition
 * and the server is the authority on whether the catalog accepts it. F03
 * records; F03b refuses — nothing here is an authorization decision.
 */
export function useDefinePermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DefinePermissionInput) =>
      api.permissions.definePermission(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.permissions.catalog(),
      });
    },
  });
}

/*
 * Replace-set-permissions mutation (F03 R49). The composer's save: the whole
 * membership the administrator selected is submitted and becomes the whole of
 * the set — Identity applies it as one act (AC-7d54eb56), drops any permission
 * not named (AC-c9d9d80a) and leaves the set as it was on failure
 * (AC-e9637517).
 *
 * Mutate → invalidate, NO optimistic update — this is a set-composition write
 * and the server (Identity, relayed by the domain service) is the authority on
 * what the set contains after the change (product rule: no optimistic updates
 * on these set-composition writes; the server is the authority). On success the
 * set list and that set's current composition are invalidated so the composer,
 * reopened, reads the authoritative membership and shows it as already chosen
 * (AC-cf93b725). The error is NOT swallowed: a refusal propagates so the
 * composer can surface it where the operator works and the save is never shown
 * as success. F03 records; F03b refuses — nothing here is an authorization
 * decision.
 */
export function useReplaceSetPermissions(setId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReplaceSetPermissionsInput) =>
      api.permissions.replaceSetPermissions(setId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.permissionSets.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: keys.permissionSets.composition(setId),
      });
    },
  });
}
